import {useNavigation} from '@react-navigation/native';
import uniqBy from 'lodash/uniqBy';
import {MoodLightCategory, MoodLightDto} from 'puffco-api-axios-client';
import React from 'react';
import {SectionList, StyleSheet, View} from 'react-native';
import {useSelector} from 'react-redux';
import {useStateWithHistory} from 'react-use';

import {Alert} from '../components/Alert';
import {MOOD_TYPE_NAME_RECORD} from '../constants';
import {Alerts, Strings, interpolate} from '../constants/Strings';
import {Navigators, Screens} from '../constants/navigation';
import {useSpinner} from '../contexts/spinner';
import {DeleteIcon, DuplicateIcon, PlusIcon, ShareIcon} from '../icons';
import {useDeleteMoodLight} from '../lib/hooks/useDeleteMoodLight';
import {useDuplicateMoodLight} from '../lib/hooks/useDuplicateMoodLight';
import {useHasLed3Api} from '../lib/hooks/useHasLed3Api';
import {
  useLoadAccountMoodLights,
  useLoadExclusiveMoodLights,
} from '../lib/hooks/useLoadMoodLights';
import {useShareMoodLight} from '../lib/hooks/useShareMoodLight';
import {useTheme} from '../lib/hooks/useTheme';
import {connectedPeakSelector} from '../lib/redux/slices/bleSlice';
import {
  accountMoodLightsSelector,
  allAndCurrentDeviceMoodLightsSelector,
  currentDeviceExclusiveMoodLightsSelector,
  currentDeviceMoodLightsSelector,
} from '../lib/redux/slices/moodLightSlice';
import {userSelector} from '../lib/redux/slices/userSlice';
import {
  MoodLight,
  MoodType,
  User,
  isCustomMoodLight,
  isExclusiveMoodLight,
} from '../lib/types';
import {elapsedSecondsSince, isAdvancedMoodLight} from '../lib/utils';
import type {
  RootStackParamList,
  RootStackScreenProps,
} from '../navigators/RootStackNavigator';
import {analytics} from '../services/analytics';
import {centerStyle, colors} from '../styles';
import {BottomSheetComponent} from './BottomSheet';
import {BottomSheetDetail} from './BottomSheetDetail';
import {MoodLightCard} from './MoodLightCard';
import {MoodLightIcon} from './MoodLightIcon';
import {MultiGradientBackground} from './MultiGradientBackground';
import {SectionTitle} from './SectionTitle';

interface Props {
  selectedId?: string;
  onItemPress?: (value?: MoodLightDto) => void;
  onItemToggle?: (value?: MoodLightDto) => void;
}

type Navigation = RootStackScreenProps<keyof RootStackParamList>['navigation'];

const descendingByCreatedAt = (m1: MoodLight, m2: MoodLight) =>
  new Date(m2.created).getTime() - new Date(m1.created).getTime();

const isNewPeakMood = (user?: User) => (item: MoodLight) =>
  item.category === MoodLightCategory.Exclusive || !item.user?.id || !user?.id
    ? false
    : item.user.id !== user.id;

export const MoodLightList: React.FC<Props> = ({
  selectedId: initialSelectedId,
  onItemPress,
  onItemToggle,
}) => {
  const [selectedId, setSelectedId, selectedIdHistory] = useStateWithHistory(
    initialSelectedId ?? '',
    2,
  );

  const theme = useTheme();
  const navigation = useNavigation<Navigation>();

  const peak = useSelector(connectedPeakSelector);
  const user = useSelector(userSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const account = useSelector(accountMoodLightsSelector);
  const exclusive = useSelector(currentDeviceExclusiveMoodLightsSelector);

  const currentDeviceMoodLights = useSelector(currentDeviceMoodLightsSelector);

  const newPeakMoods = uniqBy(
    currentDeviceMoodLights.filter(
      m =>
        isNewPeakMood(user)(m) &&
        !account.find(ml => ml.projector.meta.originalMoodLightId === m.id),
    ),
    ({id}) => id,
  );

  const accountWithoutNewPeakMoods = account.filter(
    item => item.user?.id === user?.id,
  );

  const [{isLoading: areAccountMoodLightLoading}, loadAccountMoodLights] =
    useLoadAccountMoodLights({
      serialNumber: peak?.serialNumber,
    });
  const [{isLoading: areExclusiveMoodLightLoading}, loadExclusiveMoodLights] =
    useLoadExclusiveMoodLights({
      serialNumber: peak?.serialNumber,
    });
  const [{loading: isSharing}, share] = useShareMoodLight();
  const [{loading: isDeleting}, deleteMoodLight] = useDeleteMoodLight();
  const [{loading: isDuplicating}, duplicateMoodLight] = useDuplicateMoodLight({
    onSuccess: onSuccessDuplicate,
    onError: onError,
  });

  const hasLed3Api = useHasLed3Api();

  React.useEffect(() => {
    const lastSelectedId = selectedIdHistory.history[0];

    if (!selectedId || !lastSelectedId) return;

    if (lastSelectedId !== selectedId) {
      analytics.trackEvent(
        'lantern off',
        {},
        undefined,
        ({timestamp, ...properties}) => ({
          ...properties,
          ...(timestamp && {
            duration: elapsedSecondsSince(timestamp),
          }),
        }),
      );
    }
  }, [selectedId, selectedIdHistory]);

  React.useEffect(() => {
    onItemToggle?.(selectedId ? moodLights[selectedId] : undefined);
  }, [selectedId]);

  const onMoreDotsPress = React.useCallback(
    (m: MoodLight) => {
      const content = [
        {
          title: Strings.moodLights.buttons.add,
          icon: PlusIcon,
          size: 18,
          onPress: () => {
            navigation.navigate(Navigators.MainNavigator, {
              screen: Navigators.HomeDrawerNavigator,
              params: {
                screen: Navigators.HomeEmulatedDrawer,
                params: {
                  screen: Navigators.ControlCenter,
                  params: {
                    screen: Screens.AssignToAHeatProfile,
                    params: {
                      moodLightId: m.id,
                    },
                  },
                },
              },
            });
          },
        },
        ...(isCustomMoodLight(m)
          ? isNewPeakMood(user)(m)
            ? [
                {
                  title: Strings.moodLights.buttons.save,
                  icon: DuplicateIcon,
                  size: 23,
                  onPress: () => {
                    duplicateMoodLight(m, {keepOriginalName: true});
                  },
                },
              ]
            : [
                {
                  title: Strings.moodLights.buttons.share,
                  icon: ShareIcon,
                  size: 28,
                  onPress: () => {
                    share(m.id);
                  },
                },
                {
                  title: Strings.moodLights.buttons.duplicate,
                  icon: DuplicateIcon,
                  size: 23,
                  onPress: () => {
                    duplicateMoodLight(m);
                  },
                },
                {
                  title: Strings.moodLights.buttons.delete,
                  icon: DeleteIcon,
                  size: 28,
                  onPress: () => {
                    deleteMoodLight({
                      id: m.id,
                      onDelete: () => {
                        analytics.trackEvent('mood light delete', {});
                      },
                    });
                  },
                },
              ]
          : []),
      ];

      const subtitle = isExclusiveMoodLight(m)
        ? Strings.EXCLUSIVE
        : `${MOOD_TYPE_NAME_RECORD[m.projector.meta.moodType as MoodType]} ${m.projector.meta.tempoFrac && `• ${Math.round(m.projector.meta.tempoFrac * 100)}%`}`;

      BottomSheetComponent.display(
        <BottomSheetDetail
          header={{
            icon: (
              <MultiGradientBackground
                gradientShadeFunction={
                  theme.bottomSheetTheme.gradientShadeFunction
                }
                colors={m.projector.meta.userColors ?? []}
                style={{...centerStyle}}>
                <MoodLightIcon
                  style={{height: 24, width: 24, backgroundColor: undefined}}
                  iconStyle={{height: '100%', width: '100%'}}
                />
              </MultiGradientBackground>
            ),
            title: m.name,
            subtitle,
          }}
          buttonPropArr={content}
        />,
      );
    },
    [
      deleteMoodLight,
      duplicateMoodLight,
      share,
      user,
      theme.bottomSheetTheme.gradientShadeFunction,
    ],
  );

  useSpinner({
    isVisible:
      areAccountMoodLightLoading ||
      areExclusiveMoodLightLoading ||
      isSharing ||
      isDeleting ||
      isDuplicating,
    text: isSharing
      ? Strings.PREPARING_MOOD_LIGHT_SHARE
      : isDeleting
        ? Strings.DELETING
        : isDuplicating
          ? Strings.SAVING
          : '',
    color: colors.white,
  });

  React.useEffect(() => {
    if (!user) return;

    loadAccountMoodLights();
    loadExclusiveMoodLights;
  }, [user, loadAccountMoodLights, loadExclusiveMoodLights]);

  const sections = [
    ...(newPeakMoods.length
      ? [{title: 'New Peak Moods', data: newPeakMoods}]
      : []),
    ...(accountWithoutNewPeakMoods.length
      ? [
          {
            title: Strings.moodLights.categories.custom,
            data: accountWithoutNewPeakMoods.sort(descendingByCreatedAt) ?? [],
          },
        ]
      : []),
    {
      title: Strings.moodLights.categories.exclusive,
      data: exclusive.sort(descendingByCreatedAt) ?? [],
    },
  ];

  const onMoodLightCardPress = (moodLight: MoodLight) => {
    if (isExclusiveMoodLight(moodLight)) {
      navigation.navigate(Screens.ExclusiveMood, {
        moodLight,
      });

      return;
    }

    if (isAdvancedMoodLight(moodLight) && !hasLed3Api) {
      onIncompatiblePeak();

      return;
    }

    navigation.navigate(Navigators.MainNavigator, {
      screen: Navigators.MoodLightController,
      params: {
        screen: Screens.MoodLightEdit,
        params: {
          id: moodLight.id,
        },
      },
    });
  };

  const onTogglePress = (moodLight: MoodLight) => {
    const id = moodLight.id === selectedId ? '' : moodLight.id;

    setSelectedId(id);
  };

  return (
    <View style={styles.screen}>
      {!areAccountMoodLightLoading && !areExclusiveMoodLightLoading && (
        <View style={styles.list}>
          <SectionList
            sections={sections}
            keyExtractor={(item: MoodLight) => item.id.toString()}
            renderSectionHeader={({section: {title}}) => (
              <SectionTitle
                title={title}
                style={{paddingHorizontal: 8}}
                titleStyle={styles.title}
                fillerStyle={{backgroundColor: colors.white20}}
              />
            )}
            renderItem={({item: moodLight}) => (
              <MoodLightCard
                moodLight={moodLight}
                isActive={moodLight.id === selectedId}
                onSwitchValueChange={() => onTogglePress(moodLight)}
                {...(!isNewPeakMood(user)(moodLight) && {
                  onPress: () =>
                    onItemPress
                      ? onItemPress(moodLight)
                      : onMoodLightCardPress(moodLight),
                })}
                {...(!onItemPress && {
                  moreActionsSelection: () => onMoreDotsPress(moodLight),
                })}
                username={moodLight.user?.username}
              />
            )}
            nestedScrollEnabled={true}
          />
        </View>
      )}
    </View>
  );
};

const onIncompatiblePeak = () => {
  Alert.alert(
    Alerts.INCOMPATIBLE_ANIMATION_TITLE,
    Alerts.INCOMPATIBLE_ANIMATION_BODY,
    [
      {
        text: Strings.OKAY,
        style: 'cancel',
      },
    ],
  );
};

const onSuccessDuplicate = (moodLight: MoodLight) =>
  Alert.alert(
    Alerts.MOOD_DUPLICATED,
    interpolate(Alerts.MOOD_DUPLICATED_BODY, {
      name: moodLight.name,
    }),
  );

const onError = (error: Error) =>
  Alert.alert(Alerts.MOOD_LIGHT_CREATE_ERROR_TITLE, error.message);

const styles = StyleSheet.create({
  screen: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    flexBasis: 0,
  },
  title: {
    fontFamily: 'Roboto-Regular',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 16,
    marginRight: 11,
    color: colors.white,
  },
});
